import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Button, Checkbox, Chip, DialogContent, DialogTitle,
  FormControlLabel, Stack, TextField, Typography,
} from "@mui/material";
import {
  FaClipboard, FaDesktop, FaExpand, FaFileExcel, FaFilePdf, FaPlay, FaPlug, FaPowerOff,
} from "react-icons/fa";
import RFB from "@novnc/novnc";
import api from "../Api";
import Dialog from "./ConsoleDialog";
import ConsoleDownloadsDialog from "./ConsoleDownloadsDialog";
import { openPdfWindow } from "../utils/pdfWindow";

// Myntra console — a SEPARATE remote-desktop view from the Flipkart one. Its own
// display (:100), its own websocket (/myntra-ws/), its own six browsers routed
// through a residential proxy (Myntra blocks the datacenter IP).
function wsOrigin() {
  const apiBase =
    process.env.REACT_APP_API_BASE_URL_PROD ||
    process.env.REACT_APP_API_BASE_URL ||
    "";
  let origin = window.location.origin;
  try {
    if (apiBase) origin = new URL(apiBase, window.location.origin).origin;
  } catch {
    /* keep page origin */
  }
  return origin.replace(/^http/, "ws");
}

const STATUS = {
  idle: { label: "Not connected", color: "default" },
  connecting: { label: "Connecting…", color: "warning" },
  connected: { label: "Live", color: "success" },
  error: { label: "Error", color: "error" },
  closed: { label: "Disconnected", color: "default" },
};

const fmtSize = (bytes) => {
  const n = Number(bytes || 0);
  return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
};

const fmtTime = (ms) => {
  try { return new Date(ms).toLocaleString(); }
  catch { return "-"; }
};

export default function MyntraConsolePage() {
  const screenRef = useRef(null);
  const screenShellRef = useRef(null);
  const rfbRef = useRef(null);
  const remoteFocusedRef = useRef(false);
  const remoteMouseDownRef = useRef(false);
  const remoteMouseMoveAtRef = useRef(0);
  const remotePendingDownRef = useRef(null);
  const remotePointerQueueRef = useRef(Promise.resolve());
  const pasteSinkRef = useRef(null);
  const autoPdfRef = useRef(null);
  const runDialogDragRef = useRef(null);
  const runDialogResizeRef = useRef(null);
  const logsDialogDragRef = useRef(null);
  const logsDialogResizeRef = useRef(null);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [consoleMode, setConsoleMode] = useState("remote");
  const [accounts, setAccounts] = useState([]);
  const [active, setActive] = useState("");
  const [switching, setSwitching] = useState("");
  const [remoteClip, setRemoteClip] = useState("");
  const [remoteCopied, setRemoteCopied] = useState(false);
  const [remoteCopyBlocked, setRemoteCopyBlocked] = useState(false);

  const copyRemoteToLocal = useCallback(async (text) => {
    const value = String(text || remoteClip || "");
    if (!value) return false;
    try {
      await navigator.clipboard.writeText(value);
      setRemoteCopied(true);
      setRemoteCopyBlocked(false);
      setTimeout(() => setRemoteCopied(false), 1800);
      return true;
    } catch {
      setRemoteClip(value);
      setRemoteCopyBlocked(true);
      return false;
    }
  }, [remoteClip]);
  useEffect(() => {
    const onPaste = (event) => {
      const r = rfbRef.current;
      if (!r || !remoteFocusedRef.current) return;
      const text = event.clipboardData?.getData("text/plain") || "";
      if (!text) return;
      event.preventDefault();
      r.clipboardPasteFrom(text);
      setTimeout(() => {
        r.sendKey(0xffe3, "ControlLeft", true);
        r.sendKey(0x76, "KeyV", true);
        r.sendKey(0x76, "KeyV", false);
        r.sendKey(0xffe3, "ControlLeft", false);
        r.focus();
        pasteSinkRef.current?.remove();
        pasteSinkRef.current = null;
      }, 80);
    };
    window.addEventListener("paste", onPaste, true);
    return () => window.removeEventListener("paste", onPaste, true);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!event.metaKey || String(event.key).toLowerCase() !== "v") return;
      if (!remoteFocusedRef.current || !rfbRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      const r = rfbRef.current;
      r.sendKey(0xffe3, "ControlLeft", true);
      r.sendKey(0x76, "KeyV", true);
      r.sendKey(0x76, "KeyV", false);
      r.sendKey(0xffe3, "ControlLeft", false);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);

  // Operations that can run on the logged-in Myntra browsers. Starting with OTC
  // fetch; more (in-transit, etc.) will be added the same way.
  const [picked, setPicked] = useState([]);
  const [ops, setOps] = useState(["otc"]);
  const [running, setRunning] = useState(false);
  const [runLog, setRunLog] = useState("");
  const [logOpen, setLogOpen] = useState(false);
  const [runDialogPos, setRunDialogPos] = useState(null);
  const [runDialogSize, setRunDialogSize] = useState(null);
  const [dlOpen, setDlOpen] = useState(false);
  const [mergeFilesOpen, setMergeFilesOpen] = useState(false);
  const [mergeFilesLoading, setMergeFilesLoading] = useState(false);
  const [mergeFiles, setMergeFiles] = useState([]);
  const [mergeFilesSearch, setMergeFilesSearch] = useState("");
  const [mergeFilesShowSub, setMergeFilesShowSub] = useState(false);
  const pollRef = useRef(null);
  const autoLoginAttemptedRef = useRef(new Set());
  // Read-only DB viewer (myntra.db)
  const [dbOpen, setDbOpen] = useState(false);
  const [dbTables, setDbTables] = useState([]);
  const [dbTable, setDbTable] = useState("");
  const [dbData, setDbData] = useState(null);
  const [dbSearch, setDbSearch] = useState("");
  const [dbLoading, setDbLoading] = useState(false);
  // Tools: SKU / Seals / Merge action results
  const [toolOpen, setToolOpen] = useState(false);
  const [toolTitle, setToolTitle] = useState("");
  const [toolBusy, setToolBusy] = useState(false);
  const [toolResult, setToolResult] = useState(null);
  const [runKind, setRunKind] = useState("ops"); // "ops" | "login" | "process" | "recover" | "reconcile"
  // Order Process — full "Run Account" flow (multi-account)
  const [procOpen, setProcOpen] = useState(false);
  const [procAccounts, setProcAccounts] = useState([]); // selected profiles
  const [procParallel, setProcParallel] = useState(false);
  const [procDate, setProcDate] = useState(""); // DD-MM-YYYY
  const [procTime, setProcTime] = useState(""); // HH:MM
  const [procPreSku, setProcPreSku] = useState(false);
  const [procIntransit, setProcIntransit] = useState(false);
  const [procOtc, setProcOtc] = useState(false);
  const [procMerge, setProcMerge] = useState(false);
  // Scheduler viewer + editor
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedData, setSchedData] = useState(null); // {accounts, groups}
  const [schedBusy, setSchedBusy] = useState(false);
  const [schedEdit, setSchedEdit] = useState(null); // null | {mode, time, days[], account_ids[], flags…, delete_ids[]}
  const [schedSaving, setSchedSaving] = useState(false);
  // Log viewer
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsDialogPos, setLogsDialogPos] = useState(null);
  const [logsDialogSize, setLogsDialogSize] = useState(null);
  const [logsFiles, setLogsFiles] = useState(null);
  const [logsBusy, setLogsBusy] = useState(false);
  const [runLogRows, setRunLogRows] = useState(null);
  // Seal Add
  const [sealAddOpen, setSealAddOpen] = useState(false);
  const [sealAddText, setSealAddText] = useState("");
  const [sealAddBusy, setSealAddBusy] = useState(false);
  const [sealAddResult, setSealAddResult] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetProfiles, setResetProfiles] = useState([]);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await api.get("/api/myntra-console/accounts");
      const list = data?.accounts || [];
      const canRun = (a) => a.running && !a.login && !a.denied;
      setAccounts(list);
      setPicked((prev) => {
        const allowed = new Set(list.map((a) => a.profile));
        const kept = prev.filter((profile) => allowed.has(profile));
        if (kept.length) return kept;
        const runnable = list.filter(canRun).map((a) => a.profile);
        return runnable.length ? runnable : list.map((a) => a.profile);
      });
    } catch {
      setAccounts([]);
    }
  }, []);

  const toggle = (list, setList, key) =>
    setList(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);

  const toggleOp = useCallback((key) => {
    setOps((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      const next = prev.filter((k) => {
        if (key === "intransit") return k !== "intransit_latest";
        if (key === "intransit_latest") return k !== "intransit";
        return true;
      });
      return [...next, key];
    });
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const accountNeedsLogin = useCallback((a) => (
    a?.running && (a.login || a.denied)
  ), []);

  const clampDialogPos = useCallback((x, y, width = 720, height = 420) => {
    const margin = 8;
    const minVisibleX = Math.min(160, Math.max(80, width / 3));
    const minVisibleY = 80;
    const maxX = Math.max(margin, window.innerWidth - minVisibleX);
    const maxY = Math.max(margin, window.innerHeight - minVisibleY);
    return {
      x: Math.min(Math.max(margin, x), maxX),
      y: Math.min(Math.max(margin, y), maxY),
    };
  }, []);

  const clampDialogSize = useCallback((width, height) => {
    const margin = 8;
    return {
      width: Math.min(Math.max(420, width), Math.max(420, window.innerWidth - margin * 2)),
      height: Math.min(Math.max(260, height), Math.max(260, window.innerHeight - margin * 2)),
    };
  }, []);

  const stopDialogDrag = useCallback((dragRef) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (drag?.move) window.removeEventListener("pointermove", drag.move);
    if (drag?.stop) {
      window.removeEventListener("pointerup", drag.stop);
      window.removeEventListener("pointercancel", drag.stop);
    }
  }, []);

  const startDialogDrag = useCallback((event, dragRef, setPos) => {
    if (event.button !== 0) return;
    if (event.target.closest("button,a,input,textarea,select,[role='button']")) return;
    const paper = event.currentTarget.closest(".MuiPaper-root");
    if (!paper) return;
    const rect = paper.getBoundingClientRect();
    const drag = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      move: (moveEvent) => {
        const current = dragRef.current;
        if (!current) return;
        setPos(clampDialogPos(
          moveEvent.clientX - current.offsetX,
          moveEvent.clientY - current.offsetY,
          current.width,
          current.height
        ));
      },
      stop: () => stopDialogDrag(dragRef),
    };
    dragRef.current = drag;
    setPos(clampDialogPos(rect.left, rect.top, rect.width, rect.height));
    window.addEventListener("pointermove", drag.move);
    window.addEventListener("pointerup", drag.stop);
    window.addEventListener("pointercancel", drag.stop);
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* pointer capture is best-effort */ }
    event.preventDefault();
  }, [clampDialogPos, stopDialogDrag]);

  const stopDialogResize = useCallback((resizeRef) => {
    const resize = resizeRef.current;
    resizeRef.current = null;
    if (resize?.move) window.removeEventListener("pointermove", resize.move);
    if (resize?.stop) {
      window.removeEventListener("pointerup", resize.stop);
      window.removeEventListener("pointercancel", resize.stop);
    }
  }, []);

  const startDialogResize = useCallback((event, resizeRef, setSize) => {
    if (event.button !== 0) return;
    const paper = event.currentTarget.closest(".MuiPaper-root");
    if (!paper) return;
    const rect = paper.getBoundingClientRect();
    const resize = {
      startX: event.clientX,
      startY: event.clientY,
      width: rect.width,
      height: rect.height,
      move: (moveEvent) => {
        const current = resizeRef.current;
        if (!current) return;
        setSize(clampDialogSize(
          current.width + moveEvent.clientX - current.startX,
          current.height + moveEvent.clientY - current.startY
        ));
      },
      stop: () => stopDialogResize(resizeRef),
    };
    resizeRef.current = resize;
    setSize(clampDialogSize(rect.width, rect.height));
    window.addEventListener("pointermove", resize.move);
    window.addEventListener("pointerup", resize.stop);
    window.addEventListener("pointercancel", resize.stop);
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* pointer capture is best-effort */ }
    event.preventDefault();
    event.stopPropagation();
  }, [clampDialogSize, stopDialogResize]);

  const startRunDialogDrag = useCallback((event) => {
    startDialogDrag(event, runDialogDragRef, setRunDialogPos);
  }, [startDialogDrag]);

  const startLogsDialogDrag = useCallback((event) => {
    startDialogDrag(event, logsDialogDragRef, setLogsDialogPos);
  }, [startDialogDrag]);

  const startRunDialogResize = useCallback((event) => {
    startDialogResize(event, runDialogResizeRef, setRunDialogSize);
  }, [startDialogResize]);

  const startLogsDialogResize = useCallback((event) => {
    startDialogResize(event, logsDialogResizeRef, setLogsDialogSize);
  }, [startDialogResize]);

  useEffect(() => () => {
    stopDialogDrag(runDialogDragRef);
    stopDialogResize(runDialogResizeRef);
    stopDialogDrag(logsDialogDragRef);
    stopDialogResize(logsDialogResizeRef);
  }, [stopDialogDrag, stopDialogResize]);

  const runSelected = useCallback(async () => {
    setMessage("");
    setRunKind("ops");
    setRunLog("Queued — waiting for the runner…\n");
    setLogOpen(true);
    setRunning(true);
    try {
      const macOtc = ops.length === 1 && ops[0] === "otc";
      const { data } = await api.post(
        macOtc ? "/api/myntra-console/mac-otc" : "/api/myntra-console/run",
        { accounts: picked, ops }
      );
      const id = data.id;
      const pollUrl = macOtc
        ? `/api/myntra-console/mac-otc/${id}`
        : `/api/myntra-console/run/${id}`;
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(pollUrl);
          if (st.log) setRunLog(st.log);
          if (st.done) {
            stopPolling();
            setRunning(false);
          }
        } catch {
          /* keep polling */
        }
      }, 2000);
    } catch (err) {
      setRunning(false);
      setRunLog("");
      setLogOpen(false);
      setMessage(err?.response?.data?.error || err?.message || "Could not start the run.");
    }
  }, [picked, ops, stopPolling]);

  const startAutoLogin = useCallback(async (profiles = null, source = "manual") => {
    if (running) return;
    const targets = (Array.isArray(profiles) && profiles.length ? profiles
      : picked.length ? picked
        : accounts.filter(accountNeedsLogin).map((a) => a.profile))
      .filter(Boolean);
    const uniqueTargets = Array.from(new Set(targets));
    if (!uniqueTargets.length) {
      if (source === "manual") setMessage("Tick at least one Myntra account for Auto Login.");
      return;
    }
    setMessage("");
    setRunKind("login");
    setRunLog(`Queued - auto-login for ${uniqueTargets.length} Myntra account(s)...\n`);
    setLogOpen(true);
    setRunning(true);
    try {
      const { data } = await api.post("/api/myntra-console/mac-login", {
        accounts: uniqueTargets,
        force: source === "manual",
      });
      const id = data.id;
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/api/myntra-console/mac-login/${id}`);
          if (st.log) setRunLog(st.log);
          if (st.done) {
            stopPolling();
            setRunning(false);
            loadAccounts();
          }
        } catch {
          /* keep polling */
        }
      }, 2000);
    } catch (err) {
      setRunning(false);
      setRunLog("");
      setLogOpen(false);
      setMessage(err?.response?.data?.error || err?.message || "Could not start auto-login.");
    }
  }, [accountNeedsLogin, accounts, loadAccounts, picked, running, stopPolling]);

  const loadTable = useCallback(async (table, search) => {
    setDbLoading(true);
    try {
      const { data } = await api.get("/api/myntra-console/db/rows", {
        params: { table, search: search || "", limit: 200 },
      });
      setDbData(data && data.ok ? data : null);
    } catch {
      setDbData(null);
    }
    setDbLoading(false);
  }, []);

  const openDb = useCallback(async () => {
    setDbOpen(true);
    try {
      const { data } = await api.get("/api/myntra-console/db/tables");
      const tabs = (data && data.tables) || [];
      setDbTables(tabs);
      const first = tabs.find((t) => t.name === "accounts") || tabs[0];
      if (first) { setDbTable(first.name); setDbSearch(""); loadTable(first.name, ""); }
    } catch {
      setDbTables([]);
    }
  }, [loadTable]);

  const openMynFile = useCallback(async (relpath, dl, claimedWindow = null) => {
    const win = dl ? null : (claimedWindow || openPdfWindow());
    try {
      const { data } = await api.get("/api/myntra-console/file", {
        params: { p: relpath, ...(dl ? { dl: 1 } : {}) },
        responseType: "blob",
      });
      const ext = (relpath.split(".").pop() || "").toLowerCase();
      const type = ext === "pdf" ? "application/pdf"
        : ext === "html" ? "text/html"
        : ext === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/octet-stream";
      const blob = new Blob([data], { type });
      if (dl) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = relpath.split("/").pop() || "file";
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        win.show(blob, relpath.split("/").pop() || "myntra.pdf");
      }
    } catch (err) {
      if (win) win.fail();
      setMessage(err?.response?.data?.error || err?.message || "Could not fetch file.");
    }
  }, []);

  const loadMergeFiles = useCallback(async () => {
    setMergeFilesLoading(true);
    try {
      const { data } = await api.get("/api/myntra-console/merge-files", {
        params: { search: mergeFilesSearch.trim(), limit: 500, ...(mergeFilesShowSub ? { sub: 1 } : {}) },
      });
      setMergeFiles(data?.ok && Array.isArray(data.files) ? data.files : []);
    } catch (err) {
      setMessage(err?.response?.data?.error || err?.message || "Could not load merge files.");
      setMergeFiles([]);
    } finally {
      setMergeFilesLoading(false);
    }
  }, [mergeFilesSearch, mergeFilesShowSub]);

  useEffect(() => {
    if (mergeFilesOpen) loadMergeFiles();
  }, [loadMergeFiles, mergeFilesOpen]);

  const runAction = useCallback(async (name, kind, claimedWindow = null) => {
    setToolTitle(name === "sku" ? "SKU List" : name === "seals" ? "Seals" : "Merge labels");
    setToolResult(null);
    setToolBusy(true);
    setToolOpen(true);
    try {
      const { data } = await api.post(`/api/myntra-console/action/${name}`, kind ? { kind } : {});
      setToolResult(data);
      if (name === "merge") {
        const first = data?.outputs?.[0];
        if (data?.ok && first) await openMynFile(first, false, claimedWindow);
        else if (claimedWindow) claimedWindow.fail();
      }
    } catch (err) {
      if (claimedWindow) claimedWindow.fail();
      setToolResult({ ok: false, error: err?.response?.data?.error || err?.message || "failed" });
    }
    setToolBusy(false);
  }, [openMynFile]);

  const submitProcess = useCallback(async () => {
    if (!procAccounts.length) { setMessage("Select at least one account."); return; }
    // date DD-MM-YYYY + time HH:MM -> cutoff YYYY-MM-DD HH:MM
    const dm = procDate.trim().match(/^(\d{2})-(\d{2})-(\d{4})$/);
    const tm = procTime.trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!dm || !tm) { setMessage("Use DD-MM-YYYY for date and HH:MM for time."); return; }
    const cutoff = `${dm[3]}-${dm[2]}-${dm[1]} ${tm[1].padStart(2, "0")}:${tm[2]}`;
    setProcOpen(false);
    setRunKind("process");
    setRunLog("Queued — starting the packing flow…\n");
    setLogOpen(true);
    setRunning(true);
    try {
      const { data } = await api.post("/api/myntra-console/process", {
        profiles: procAccounts,
        cutoff,
        parallel: procParallel && procAccounts.length > 1,
        pre_sku: procPreSku,
        intransit: procIntransit,
        otc: procOtc,
        merge: procMerge,
      });
      const id = data.id;
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/api/myntra-console/run/${id}`);
          if (st.log) setRunLog(st.log);
          if (st.done) { stopPolling(); setRunning(false); }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (err) {
      setRunning(false);
      setLogOpen(false);
      setMessage(err?.response?.data?.error || err?.message || "Could not start packing.");
    }
  }, [procAccounts, procDate, procTime, procParallel, procPreSku, procIntransit, procOtc, procMerge, stopPolling]);

  const loadSchedules = useCallback(async () => {
    setSchedBusy(true);
    try {
      const { data } = await api.get("/api/myntra-console/schedules");
      setSchedData(data && data.ok ? data : null);
    } catch { setSchedData(null); }
    setSchedBusy(false);
  }, []);

  const openScheduler = useCallback(async () => {
    setSchedOpen(true);
    setSchedEdit(null);
    setSchedData(null);
    await loadSchedules();
  }, [loadSchedules]);

  const DAYS7 = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  const startAddSched = useCallback(() => {
    setSchedEdit({
      mode: "add", time: "09:00", days: [...DAYS7], account_ids: [],
      sku: false, merge: false, print: false, transit_dl: false, transit_otc: false,
      enabled: true, delete_ids: [],
    });
  }, []);

  const startEditSched = useCallback((g) => {
    setSchedEdit({
      mode: "edit", time: g.time, days: [...(g.days || [])],
      account_ids: [...(g.account_ids || [])],
      sku: !!g.sku, merge: !!g.merge, print: !!g.print,
      transit_dl: !!g.transit_dl, transit_otc: !!g.transit_otc,
      enabled: !!g.enabled, delete_ids: [...(g.schedule_ids || [])],
    });
  }, []);

  const saveSched = useCallback(async () => {
    if (!schedEdit) return;
    setSchedSaving(true);
    try {
      const { data } = await api.post("/api/myntra-console/schedules/save", schedEdit);
      if (data && data.ok) { setSchedEdit(null); await loadSchedules(); }
      else setMessage(data?.error || "Save failed.");
    } catch (err) {
      setMessage(err?.response?.data?.error || err?.message || "Save failed.");
    }
    setSchedSaving(false);
  }, [schedEdit, loadSchedules]);

  const deleteSched = useCallback(async (g) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Delete the ${g.time} schedule (${g.account_ids.length} account(s))?`)) return;
    try {
      await api.post("/api/myntra-console/schedules/delete", { ids: g.schedule_ids });
      await loadSchedules();
    } catch (err) { setMessage(err?.response?.data?.error || err?.message || "Delete failed."); }
  }, [loadSchedules]);

  const toggleSched = useCallback(async (g) => {
    try {
      await api.post("/api/myntra-console/schedules/toggle", { ids: g.schedule_ids, enabled: !g.enabled });
      await loadSchedules();
    } catch (err) { setMessage(err?.response?.data?.error || err?.message || "Toggle failed."); }
  }, [loadSchedules]);

  const openLogs = useCallback(async () => {
    setLogsOpen(true);
    setLogsBusy(true);
    setLogsFiles(null);
    setRunLogRows(null);
    try {
      const [files, rows] = await Promise.all([
        api.get("/api/myntra-console/logs/list").then((r) => r.data).catch(() => null),
        api.get("/api/myntra-console/db/rows", { params: { table: "run_log", limit: 40 } })
          .then((r) => r.data).catch(() => null),
      ]);
      setLogsFiles(files && files.ok ? files.files : []);
      setRunLogRows(rows && rows.ok ? rows : null);
    } catch { setLogsFiles([]); }
    setLogsBusy(false);
  }, []);

  const submitSealAdd = useCallback(async () => {
    if (!sealAddText.trim()) return;
    setSealAddBusy(true);
    setSealAddResult(null);
    try {
      const { data } = await api.post("/api/myntra-console/action/seals-add", {
        seals: sealAddText,
      });
      setSealAddResult(data);
      if (data && data.ok) setSealAddText("");
    } catch (err) {
      setSealAddResult({ ok: false, error: err?.response?.data?.error || err?.message || "failed" });
    }
    setSealAddBusy(false);
  }, [sealAddText]);

  const openResetBrowsers = useCallback(() => {
    const initial = picked.length ? picked : (active ? [active] : []);
    setResetProfiles(initial);
    setResetResult(null);
    setResetOpen(true);
  }, [active, picked]);

  const submitResetBrowsers = useCallback(async () => {
    if (!resetProfiles.length) {
      setResetResult({ ok: false, error: "Select account." });
      return;
    }
    setResetBusy(true);
    setResetResult(null);
    try {
      const { data } = await api.post("/api/myntra-console/reset", { accounts: resetProfiles });
      setResetResult(data);
      await loadAccounts();
    } catch (err) {
      setResetResult({
        ok: false,
        error: err?.response?.data?.error || err?.message || "reset failed",
        results: err?.response?.data?.results || [],
      });
    } finally {
      setResetBusy(false);
    }
  }, [loadAccounts, resetProfiles]);

  const runRecover = useCallback(async () => {
    setRunKind("recover");
    setRunLog("Queued — starting PDF repair/recovery…\n");
    setLogOpen(true);
    setRunning(true);
    try {
      const { data } = await api.post("/api/myntra-console/recover", {});
      const id = data.id;
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/api/myntra-console/run/${id}`);
          if (st.log) setRunLog(st.log);
          if (st.done) { stopPolling(); setRunning(false); }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (err) {
      setRunning(false);
      setLogOpen(false);
      setMessage(err?.response?.data?.error || err?.message || "Could not start recovery.");
    }
  }, [stopPolling]);

  const runReconcile = useCallback(async () => {
    if (!picked.length) {
      setMessage("Tick at least one account for reconcile.");
      return;
    }
    setMessage("");
    setRunKind("reconcile");
    setRunLog("Queued - starting reconcile...\n");
    setLogOpen(true);
    setRunning(true);
    autoPdfRef.current = openPdfWindow();
    try {
      const { data } = await api.post("/api/myntra-console/reconcile", { accounts: picked });
      const id = data.id;
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/api/myntra-console/run/${id}`);
          if (st.log) setRunLog(st.log);
          if (st.done) {
            stopPolling();
            setRunning(false);
            const openPath = st.artifacts?.open || st.artifacts?.outputs?.[0] || "";
            if (openPath) {
              await openMynFile(openPath, false, autoPdfRef.current);
            } else {
              if (autoPdfRef.current) autoPdfRef.current.fail();
              setMessage("Reconcile finished but no merged PDF was created.");
            }
            autoPdfRef.current = null;
          }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (err) {
      if (autoPdfRef.current) autoPdfRef.current.fail();
      autoPdfRef.current = null;
      setRunning(false);
      setLogOpen(false);
      setMessage(err?.response?.data?.error || err?.message || "Could not start reconcile.");
    }
  }, [openMynFile, picked, stopPolling]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    if (!accounts.length || running) return;
    for (const account of accounts) {
      if (account.loggedIn) autoLoginAttemptedRef.current.delete(account.profile);
    }
    const targets = accounts
      .filter(accountNeedsLogin)
      .filter((a) => !autoLoginAttemptedRef.current.has(a.profile))
      .map((a) => a.profile);
    if (!targets.length) return;
    targets.forEach((profile) => autoLoginAttemptedRef.current.add(profile));
    startAutoLogin(targets, "auto");
  }, [accountNeedsLogin, accounts, running, startAutoLogin]);

  const switchTo = useCallback(
    async (profile) => {
      setSwitching(profile);
      setMessage("");
      try {
        await api.post("/api/myntra-console/switch", { profile });
        setActive(profile);
        loadAccounts();
      } catch (err) {
        setMessage(
          err?.response?.data?.error || err?.message || `Could not switch to ${profile}.`
        );
      } finally {
        setSwitching("");
      }
    },
    [loadAccounts]
  );

  const cleanup = useCallback(() => {
    if (rfbRef.current) {
      try {
        rfbRef.current.disconnect();
      } catch {
        /* ignore */
      }
      rfbRef.current = null;
    }
    setStatus((prev) => (prev === "connected" ? "idle" : prev));
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get("/api/myntra-console/status")
      .then(({ data }) => {
        if (!cancelled) setConsoleMode(data?.mode === "mac" ? "mac" : "remote");
      })
      .catch(() => {
        // Keep the remote default when the status probe is unavailable.
      });
    return () => { cancelled = true; };
  }, []);

  const connect = useCallback(async () => {
    cleanup();
    setMessage("");
    setStatus("connecting");
    try {
      if (consoleMode === "mac") {
        setStatus("connected");
        setMessage("Mac Chrome mode active. Use an account button to show its real Chrome window.");
        return;
      }
      const { data } = await api.get("/api/myntra-console/ticket");
      const url = `${wsOrigin()}${data.wsPath || "/myntra-ws/"}?ticket=${encodeURIComponent(
        data.ticket
      )}`;
      const rfb = new RFB(screenRef.current, url, {});
      rfb.viewOnly = false;
      rfb.focusOnClick = true;
      rfb.scaleViewport = true;
      rfb.resizeSession = true;
      rfb.clipViewport = false;
      rfb.background = "#0b0b0b";
      rfb.addEventListener("connect", () => setStatus("connected"));
      rfb.addEventListener("disconnect", (e) => {
        setStatus(e?.detail?.clean ? "closed" : "error");
        if (!e?.detail?.clean) setMessage("Connection dropped. Reconnect to retry.");
      });
      rfb.addEventListener("securityfailure", (e) => {
        setStatus("error");
        setMessage(e?.detail?.reason || "Access denied.");
      });
      rfb.addEventListener("clipboard", (e) => {
        const text = String(e?.detail?.text || "");
        if (!text) return;
        setRemoteClip(text);
        copyRemoteToLocal(text);
      });
      rfbRef.current = rfb;
    } catch (err) {
      setStatus("error");
      setMessage(
        err?.response?.status === 403
          ? "You do not have access to the Myntra Console."
          : err?.response?.data?.error || err?.message || "Could not start the session."
      );
    }
  }, [cleanup, consoleMode, copyRemoteToLocal]);

  const fullscreen = useCallback(() => {
    const el = screenShellRef.current || screenRef.current;
    if (el?.requestFullscreen) el.requestFullscreen();
  }, []);

  const focusRemote = useCallback(async () => {
    remoteFocusedRef.current = true;
    try { rfbRef.current?.focus?.(); } catch { /* ignore */ }
    try {
      const text = await navigator.clipboard.readText();
      if (text && rfbRef.current) rfbRef.current.clipboardPasteFrom(text);
    } catch {
      /* Native paste remains available when clipboard permission is denied. */
    }
  }, []);

  const remotePointFromEvent = useCallback((event) => {
    const root = screenShellRef.current || screenRef.current;
    if (!root) return null;
    const canvas = screenRef.current?.querySelector("canvas") || root.querySelector("canvas");
    const target = canvas || root;
    const rect = target.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const r = rfbRef.current;
    const fbW = Number(r?._fbWidth || r?._display?._fbWidth || canvas?.width || 1600);
    const fbH = Number(r?._fbHeight || r?._display?._fbHeight || canvas?.height || 900);
    const x = Math.max(0, Math.min(fbW - 1, Math.round(((event.clientX - rect.left) / rect.width) * fbW)));
    const y = Math.max(0, Math.min(fbH - 1, Math.round(((event.clientY - rect.top) / rect.height) * fbH)));
    return { x, y };
  }, []);

  const relayRemoteMouse = useCallback((event, type) => {
    if (status !== "connected") return;
    const point = remotePointFromEvent(event);
    if (!point) return;
    const button = event.button === 1 ? 2 : event.button === 2 ? 3 : 1;
    if (type === "move" && !remoteMouseDownRef.current) return;
    if (type === "move") {
      const now = Date.now();
      if (now - remoteMouseMoveAtRef.current < 40) return;
      remoteMouseMoveAtRef.current = now;
    }
    event.preventDefault();
    event.stopPropagation();
    remoteFocusedRef.current = true;
    try { rfbRef.current?.focus?.(); } catch { /* ignore */ }

    const enqueuePointer = (payload) => {
      remotePointerQueueRef.current = remotePointerQueueRef.current
        .catch(() => {})
        .then(() => api.post("/api/myntra-console/pointer", payload))
        .catch((err) => {
          setMessage(err?.response?.data?.error || err?.message || "Remote click failed.");
        });
    };

    if (type === "down") {
      remoteMouseDownRef.current = true;
      remotePendingDownRef.current = { point, button };
      return;
    }

    if (type === "move") {
      const pending = remotePendingDownRef.current;
      if (pending) {
        const dx = Math.abs(point.x - pending.point.x);
        const dy = Math.abs(point.y - pending.point.y);
        if (dx <= 3 && dy <= 3) return;
        remotePendingDownRef.current = null;
        enqueuePointer({ type: "down", ...pending.point, button: pending.button, profile: active });
      }
      enqueuePointer({ type: "move", ...point, button, profile: active });
      return;
    }

    if (type === "up") {
      const pending = remotePendingDownRef.current;
      remoteMouseDownRef.current = false;
      remotePendingDownRef.current = null;
      enqueuePointer({ type: pending ? "click" : "up", ...point, button, profile: active });
    }
  }, [active, remotePointFromEvent, status]);

  const relayRemotePointer = useCallback((event, type) => {
    if (event.pointerType === "mouse" && event.button > 0) return;
    relayRemoteMouse(event, type);
  }, [relayRemoteMouse]);

  useEffect(() => {
    if (status !== "connected") return undefined;
    const insideRemote = (event) => {
      const el = screenShellRef.current;
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      return event.clientX >= rect.left && event.clientX <= rect.right
        && event.clientY >= rect.top && event.clientY <= rect.bottom;
    };
    const onPointerDown = (event) => {
      if (!insideRemote(event)) return;
      relayRemotePointer(event, "down");
    };
    const onPointerUp = (event) => {
      if (!insideRemote(event)) return;
      relayRemotePointer(event, "up");
    };
    const onPointerMove = (event) => {
      if (!insideRemote(event)) return;
      relayRemotePointer(event, "move");
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("pointerup", onPointerUp, true);
    window.addEventListener("pointermove", onPointerMove, true);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("pointerup", onPointerUp, true);
      window.removeEventListener("pointermove", onPointerMove, true);
    };
  }, [relayRemotePointer, status]);

  useEffect(() => cleanup, [cleanup]);

  const s = STATUS[status] || STATUS.idle;
  const INK = "#ffffff";
  const railBtn = (isActive) => ({
    textTransform: "none",
    justifyContent: "flex-start",
    px: 1,
    color: isActive ? "primary.main" : INK,
    bgcolor: isActive ? INK : "transparent",
    borderColor: "rgba(255,255,255,0.55)",
    "&:hover": {
      bgcolor: isActive ? INK : "rgba(255,255,255,0.14)",
      borderColor: INK,
    },
    "&.Mui-disabled": { color: "rgba(255,255,255,0.4)", borderColor: "rgba(255,255,255,0.2)" },
  });
  const tick = { color: "rgba(255,255,255,0.7)", p: 0.25, "&.Mui-checked": { color: INK } };
  const tickLabel = {
    m: 0,
    "& .MuiFormControlLabel-label": { fontSize: 11.5, color: INK, lineHeight: 1.2 },
  };
  // Operations available on the Myntra console (extend this list as more are added).
  const OPS = [
    { key: "otc", label: "OTC Fetch", icon: "🔢" },
    { key: "intransit", label: "In Transit", icon: "🚚" },
    { key: "intransit_latest", label: "Upload Latest CSV", icon: "⬆" },
  ];
  const accountCanRun = (a) => a.running && !a.login && !a.denied;
  const accountStateLabel = (a) => {
    if (!a.running) return "OFF";
    if (a.login) return "LOGIN";
    if (a.denied) return "NO ACCESS";
    if (a.state === "unreadable") return "UNREADABLE";
    if (a.state === "checking") return "CHECKING";
    if (a.state === "mac_worker_active") return "MAC";
    if (a.state === "unknown") return "CHECK";
    return "";
  };
  const accountRows = (showPick = true) => accounts.map((a) => (
    <Stack
      key={`${showPick ? "pick" : "switch"}-${a.profile}`}
      direction="row"
      alignItems="center"
      spacing={0.25}
      onPointerDown={(event) => event.stopPropagation()}
    >
      {showPick ? (
        <Checkbox
          size="small"
          sx={tick}
          checked={picked.includes(a.profile)}
          onChange={() => toggle(picked, setPicked, a.profile)}
          title={accountCanRun(a)
            ? `Include ${a.name} in the run`
            : `${a.name} is not ready for run, but can be selected for Auto Login`}
        />
      ) : null}
      <Button
        size="small"
        fullWidth
        variant={active === a.profile ? "contained" : "outlined"}
        disabled={switching === a.profile}
        onClick={() => switchTo(a.profile)}
        sx={{
          ...railBtn(active === a.profile),
          fontSize: 11,
          minWidth: 0,
          py: 0.15,
          overflow: "hidden",
          "& .MuiButton-startIcon, & .MuiButton-endIcon": { m: 0 },
        }}
        title={
          a.running
            ? `Show ${a.name} (port ${a.port})${accountStateLabel(a) ? ` - ${accountStateLabel(a)}` : ""}`
            : `Try showing ${a.name}; the port probe is currently stale/offline`
        }
      >
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {switching === a.profile ? "..." : a.name.replace(/^MYN /, "")}
          {accountStateLabel(a) && ` ${accountStateLabel(a)}`}
        </Box>
      </Button>
    </Stack>
  ));

  return (
    <Box
      sx={{
        p: 0,
        display: "flex",
        flexWrap: { xs: "wrap", md: "nowrap" },
        gap: { xs: 0.5, md: 0.25 },
        height: { xs: "auto", md: "calc(100vh - 56px)" },
        alignItems: "stretch",
        color: INK,
      }}
    >
      {/* LEFT RAIL — console header + connection controls, then the accounts */}
      <Stack spacing={0.5} sx={{
        order: { xs: 1, md: 0 },
        width: { xs: "calc(50% - 4px)", md: 118 },
        flexShrink: 0,
        color: INK,
        position: "relative",
        zIndex: 2,
        pointerEvents: "auto",
        maxHeight: { xs: "34vh", md: "none" },
        overflowY: "auto",
      }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <FaDesktop />
          <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
            Myntra Console
          </Typography>
        </Stack>
        <Chip
          size="small"
          label={s.label}
          color={s.color}
          sx={{ width: "100%", fontSize: 11, ...(s.color === "default" ? { color: INK } : {}) }}
        />
        {status === "connected" ? (
          <Button
            size="small"
            fullWidth
            variant="outlined"
            startIcon={<FaPowerOff />}
            onClick={cleanup}
            sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
          >
            Disconnect
          </Button>
        ) : (
          <Button
            size="small"
            fullWidth
            variant="contained"
            startIcon={<FaPlug />}
            onClick={connect}
            sx={{ ...railBtn(true), justifyContent: "center", fontSize: 11, py: 0.15 }}
          >
            {status === "connecting" ? "Connecting…" : "Connect"}
          </Button>
        )}
        <Button
          size="small"
          fullWidth
          variant="outlined"
          startIcon={<FaExpand />}
          onClick={fullscreen}
          disabled={status !== "connected"}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
        >
          Fullscreen
        </Button>
        {accounts.length > 0 && (
          <>
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, opacity: 0.85, pt: 0.5 }}>
              ACCOUNTS
            </Typography>
          {accountRows(true)}
          <Stack direction="row" spacing={0.5} sx={{ pt: 0.25 }}>
            <Button size="small" fullWidth variant="outlined"
              onClick={() => setPicked(accounts.map((a) => a.profile))}
              sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}>
              All
            </Button>
            <Button size="small" fullWidth variant="outlined" onClick={() => setPicked([])}
              sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}>
              None
            </Button>
          </Stack>
          <Button size="small" fullWidth variant="contained"
            disabled={running || !(picked.length || accounts.length)}
            onClick={() => startAutoLogin(picked.length ? picked : accounts.map((a) => a.profile), "manual")}
            sx={{
              ...railBtn(true),
              justifyContent: "center",
              fontSize: 11,
              py: 0.15,
              mt: 0.25,
              bgcolor: "#2e7d32",
              "&:hover": { bgcolor: "#1b5e20" },
            }}
            title={picked.length ? `Auto-login/check ${picked.length} selected Myntra account(s)` : "Auto-login/check all Myntra accounts"}>
            AUTO LOGIN
          </Button>
          </>
        )}
      </Stack>

      {/* CENTRE — the remote browser. Full-width on phones, middle pane on desktop. */}
      <Box
        sx={{
          order: { xs: 3, md: 0 },
          flexGrow: 1,
          flexBasis: { xs: "100%", md: "auto" },
          width: { xs: "100%", md: "auto" },
          minWidth: { xs: "100%", md: 0 },
          height: { xs: "52vh", md: "auto" },
          display: "flex",
        }}
      >
        <Box
          ref={screenShellRef}
          tabIndex={0}
          onPointerDown={focusRemote}
          sx={{
            width: "100%",
            height: "100%",
            minHeight: { xs: 340, md: 0 },
            bgcolor: "#0b0b0b",
            borderRadius: 1,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            ref={screenRef}
            sx={{
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              "& canvas": {
                pointerEvents: "none",
              },
            }}
          />
          {consoleMode === "mac" ? (
            <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ position: "absolute", inset: 0, color: "#fff", p: 3, textAlign: "center" }}>
              <FaDesktop size={28} />
              <Typography variant="h6">Mac Chrome Console</Typography>
              <Typography variant="body2" sx={{ maxWidth: 440, opacity: 0.8 }}>
                Myntra is running in real Chrome windows on this Mac. Select an account on the left to show its profile.
              </Typography>
            </Stack>
          ) : null}
          {consoleMode !== "mac" ? (
            <Box
              aria-hidden
              onPointerDownCapture={(event) => relayRemotePointer(event, "down")}
              onPointerUpCapture={(event) => relayRemotePointer(event, "up")}
              onPointerMoveCapture={(event) => relayRemotePointer(event, "move")}
              onClickCapture={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 2147483647,
                pointerEvents: "auto",
                cursor: "default",
                touchAction: "none",
                bgcolor: "transparent",
              }}
            />
          ) : null}
        </Box>
      </Box>

      {/* RIGHT RAIL — operations */}
      <Stack spacing={0.75} sx={{
        order: { xs: 2, md: 0 },
        width: { xs: "calc(50% - 4px)", md: 156 },
        flexShrink: 0,
        color: INK,
        position: "relative",
        zIndex: 2,
        pointerEvents: "auto",
        maxHeight: { xs: "34vh", md: "none" },
        overflowY: "auto",
      }}>
        {remoteCopied ? (
          <Typography variant="caption" sx={{ color: "#8ff0b3", fontWeight: 700 }}>
            Copied from remote
          </Typography>
        ) : null}
        {remoteCopyBlocked && remoteClip ? (
          <Button size="small" fullWidth variant="contained" startIcon={<FaClipboard />}
            onClick={() => copyRemoteToLocal(remoteClip)}
            sx={{ justifyContent: "center", fontSize: 11, py: 0.2 }}>
            Copy remote text
          </Button>
        ) : null}
        {/* OPERATIONS — tick an op, tick accounts on the left, Run Selected. */}
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, opacity: 0.85, pt: 0.5 }}>
          OPERATIONS
        </Typography>
        {OPS.map((o) => (
          <FormControlLabel
            key={o.key}
            sx={tickLabel}
            control={
              <Checkbox size="small" sx={tick}
                checked={ops.includes(o.key)}
                onChange={() => toggleOp(o.key)} />
            }
            label={`${o.icon} ${o.label}`}
          />
        ))}
        <Button
          size="small"
          fullWidth
          variant="contained"
          startIcon={<FaPlay />}
          disabled={running || !picked.length || !ops.length}
          onClick={runSelected}
          sx={{ ...railBtn(true), justifyContent: "center", fontSize: 11, mt: 0.5 }}
          title={picked.length && ops.length
            ? `Run ${ops.join(", ")} on ${picked.length} account(s)`
            : "Tick at least one account and one operation"}
        >
          {running ? "Running…" : "Run Selected"}
        </Button>
        {runLog && !logOpen ? (
          <Button size="small" fullWidth variant="outlined" onClick={() => setLogOpen(true)}
            sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}>
            Show log
          </Button>
        ) : null}
        <Button
          size="small"
          fullWidth
          variant="outlined"
          disabled={running}
          onClick={() => {
            const now = new Date();
            const pad = (n) => String(n).padStart(2, "0");
            setProcDate(`${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}`);
            setProcTime(`${pad(now.getHours())}:${pad(now.getMinutes())}`);
            // Seed from the accounts already ticked in the left rail; else all running.
            const initial = picked.length ? picked
              : accounts.filter((x) => x.running).map((x) => x.profile);
            setProcAccounts(initial);
            setProcParallel(initial.length > 1);   // parallel auto-on for 2+
            setProcPreSku(true);
            setProcIntransit(true);
            setProcOtc(false);
            setProcMerge(true);
            setProcOpen(true);
          }}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15, mt: 0.25 }}
          title="Run the full desktop packing flow (autopicklist → order processor) for one account"
        >
          📦 Order Process
        </Button>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, opacity: 0.85, pt: 0.5 }}>
          TOOLS
        </Typography>
        <Button size="small" fullWidth variant="outlined" onClick={openResetBrowsers}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}>
          🔄 Reset Browser
        </Button>
        <Button size="small" fullWidth variant="outlined" onClick={openDb}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}>
          🗄️ DB
        </Button>
        <Button size="small" fullWidth variant="outlined" onClick={openScheduler}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}>
          🗓️ Scheduler
        </Button>
        <Button size="small" fullWidth variant="outlined" onClick={openLogs}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}>
          📜 Log
        </Button>
        <Button size="small" fullWidth variant="outlined" onClick={() => setDlOpen(true)}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
          title="Files these browsers downloaded (packed-orders CSVs, exports)">
          📥 Downloads
        </Button>
        <Button size="small" fullWidth variant="outlined" onClick={() => runAction("sku", "custom")}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}>
          📋 SKU List
        </Button>
        <Button size="small" fullWidth variant="outlined" onClick={() => runAction("seals")}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}>
          🔖 Seals
        </Button>
        <Button size="small" fullWidth variant="outlined"
          onClick={() => { setSealAddResult(null); setSealAddOpen(true); }}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}>
          ➕ Seal Add
        </Button>
        <Button size="small" fullWidth variant="outlined" onClick={() => runAction("merge", undefined, openPdfWindow())}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}>
          📄 Merge
        </Button>
        <Button size="small" fullWidth variant="outlined" onClick={() => setMergeFilesOpen(true)}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
          title="View all Myntra merged PDF files">
          🗂️ Merge Files
        </Button>
        <Button size="small" fullWidth variant="outlined" disabled={running || !picked.length} onClick={runReconcile}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
          title="Fresh In Transit upload, DB reconcile, PDF recovery, then merge">
          🔍 Reconcile
        </Button>
        <Button size="small" fullWidth variant="outlined" disabled={running} onClick={runRecover}
          sx={{ ...railBtn(false), justifyContent: "center", fontSize: 11, py: 0.15 }}
          title="Redownload corrupt/missing label & invoice PDFs for today">
          🩹 PDF Recovery
        </Button>

        {message ? (
          <Typography variant="caption" sx={{ lineHeight: 1.25, color: "#ffcdd2" }}>
            {message}
          </Typography>
        ) : null}
      </Stack>

      {/* Run output. Closing this does not stop the run. */}
      <Dialog
        open={logOpen}
        onClose={() => setLogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...(runDialogPos
              ? { position: "fixed", top: runDialogPos.y, left: runDialogPos.x, m: 0 }
              : { position: "relative" }),
            ...(runDialogSize
              ? {
                  width: runDialogSize.width,
                  maxWidth: "calc(100vw - 16px)",
                  height: runDialogSize.height,
                  maxHeight: "calc(100vh - 16px)",
                }
              : {}),
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          onPointerDown={startRunDialogDrag}
          onDoubleClick={() => setRunDialogPos(null)}
          title="Hold and drag to move. Double-click to center."
          sx={{ py: 1, fontSize: 16, cursor: "grab", userSelect: "none", touchAction: "none" }}
        >
          {(() => {
            const titles = {
              login: running ? "Auto-login running on this Mac..." : "Auto-login finished",
              process: running ? "Order processing on this Mac…" : "Order processing finished",
              recover: running ? "PDF recovery on this Mac…" : "PDF recovery finished",
              reconcile: running ? "Reconcile running on this Mac…" : "Reconcile finished",
              ops: running ? "Running on this Mac…" : "Run finished",
            };
            return titles[runKind] || titles.ops;
          })()}
          <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>
            {runKind === "process"
              ? `Order Process · ${procAccounts.length} account(s)${procParallel && procAccounts.length > 1 ? " · parallel" : ""}`
              : runKind === "login"
                ? "Myntra login recovery only"
              : runKind === "recover"
                ? "PDF repair / recovery · all active accounts"
              : runKind === "reconcile"
                ? `In Transit · DB reconcile · PDF recovery · merge · ${picked.length} account(s)`
                : `${picked.length} account(s) · ${ops.join(", ")}`}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Box component="pre" sx={{
            m: 0, fontSize: 12, lineHeight: 1.45, whiteSpace: "pre-wrap",
            wordBreak: "break-word", height: "100%", maxHeight: runDialogSize ? "none" : "60vh", overflowY: "auto",
          }}>
            {runLog || "…"}
          </Box>
        </DialogContent>
        <Box
          onPointerDown={startRunDialogResize}
          title="Hold and drag corner to resize."
          sx={{
            position: "absolute",
            right: 0,
            bottom: 0,
            width: 24,
            height: 24,
            cursor: "nwse-resize",
            zIndex: 3,
            "&::after": {
              content: '""',
              position: "absolute",
              right: 6,
              bottom: 6,
              width: 10,
              height: 10,
              borderRight: "2px solid rgba(0,0,0,0.45)",
              borderBottom: "2px solid rgba(0,0,0,0.45)",
            },
          }}
        />
      </Dialog>

      <Dialog open={dbOpen} onClose={() => setDbOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: 16 }}>
          🗄️ Myntra DB
          <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>
            Read-only view of myntra.db · secrets hidden
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", mb: 1, gap: 0.5 }}>
            {dbTables.map((t) => (
              <Chip
                key={t.name}
                size="small"
                label={`${t.name} (${t.count != null ? t.count : "?"})`}
                color={dbTable === t.name ? "primary" : "default"}
                onClick={() => { setDbTable(t.name); setDbSearch(""); loadTable(t.name, ""); }}
                sx={{ fontSize: 11, cursor: "pointer" }}
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <TextField
              size="small"
              placeholder={dbTable ? `Search ${dbTable}…` : "Search…"}
              value={dbSearch}
              onChange={(e) => setDbSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") loadTable(dbTable, dbSearch); }}
              sx={{ flex: 1 }}
            />
            <Button size="small" variant="contained" onClick={() => loadTable(dbTable, dbSearch)}
              sx={{ textTransform: "none" }}>
              Search
            </Button>
          </Stack>
          {dbLoading ? (
            <Typography variant="body2">Loading…</Typography>
          ) : !dbData ? (
            <Typography variant="body2">Pick a table.</Typography>
          ) : (
            <>
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                {dbData.shown} of {dbData.total} row(s)
                {dbSearch ? ` matching “${dbSearch}”` : ""} · newest first
              </Typography>
              <Box sx={{ overflow: "auto", maxHeight: "60vh", mt: 0.5,
                         border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1 }}>
                <Box component="table" sx={{ borderCollapse: "collapse", fontSize: 11,
                       width: "max-content", minWidth: "100%" }}>
                  <Box component="thead">
                    <Box component="tr">
                      {dbData.columns.map((c) => (
                        <Box component="th" key={c}
                          sx={{ position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1,
                                borderBottom: "2px solid rgba(0,0,0,0.25)", px: 1, py: 0.5,
                                textAlign: "left", fontWeight: 700, whiteSpace: "nowrap" }}>
                          {c}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {dbData.rows.map((r, ri) => (
                      <Box component="tr" key={ri}
                        sx={{ "&:nth-of-type(even)": { bgcolor: "action.hover" } }}>
                        {r.map((cell, ci) => (
                          <Box component="td" key={ci} title={String(cell)}
                            sx={{ px: 1, py: 0.35, borderBottom: "1px solid rgba(0,0,0,0.08)",
                                  whiteSpace: "nowrap", maxWidth: 320, overflow: "hidden",
                                  textOverflow: "ellipsis" }}>
                            {String(cell)}
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={toolOpen} onClose={() => setToolOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: 16 }}>{toolTitle}</DialogTitle>
        <DialogContent dividers>
          {toolBusy ? (
            <Typography variant="body2">Running…</Typography>
          ) : !toolResult ? (
            <Typography variant="body2">—</Typography>
          ) : !toolResult.ok ? (
            <Typography variant="body2" sx={{ color: "error.main" }}>
              Error: {String(toolResult.error)}
            </Typography>
          ) : toolResult.report ? (
            <Stack spacing={0.5}>
              {toolResult.report.map((r, i) => (
                <Typography key={i} variant="body2">
                  {r.account_name}: <b>{r.unused}</b> unused seals — {r.is_low ? "⚠ LOW" : "OK ✅"} (threshold {r.threshold})
                </Typography>
              ))}
            </Stack>
          ) : toolResult.path !== undefined ? (
            (toolResult.empty || !toolResult.path) ? (
              <Typography variant="body2">No SKUs for today yet (nothing packed).</Typography>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                <Button size="small" variant="contained" onClick={() => openMynFile(toolResult.path, false)}
                  sx={{ textTransform: "none" }}>Open</Button>
                <Button size="small" variant="outlined" onClick={() => openMynFile(toolResult.path, true)}
                  sx={{ textTransform: "none" }}>Download</Button>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {toolResult.path.split("/").pop()}
                </Typography>
              </Stack>
            )
          ) : toolResult.outputs !== undefined ? (
            toolResult.count === 0 ? (
              <Typography variant="body2">Nothing to merge yet — no label PDFs (run order processing first).</Typography>
            ) : (
              <Stack spacing={0.75}>
                {toolResult.outputs.map((o, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="center">
                    <Button size="small" variant="contained" onClick={() => openMynFile(o, false)}
                      sx={{ textTransform: "none" }}>Open</Button>
                    <Button size="small" variant="outlined" onClick={() => openMynFile(o, true)}
                      sx={{ textTransform: "none" }}>Download</Button>
                    <Typography variant="caption" sx={{ opacity: 0.7, wordBreak: "break-all" }}>
                      {o.split("/").pop()}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )
          ) : (
            <Typography variant="body2">Done.</Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={mergeFilesOpen} onClose={() => setMergeFilesOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: 16 }}>
          🗂️ Myntra Merge Files
          <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>
            Final merge PDFs and Myntra SKU-list workbooks. Sub/account merge files are hidden by default.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1 }}>
            <TextField
              size="small"
              placeholder="Search date, account, file..."
              value={mergeFilesSearch}
              onChange={(e) => setMergeFilesSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") loadMergeFiles(); }}
              sx={{ flex: 1 }}
            />
            <Button size="small" variant="contained" onClick={loadMergeFiles}
              disabled={mergeFilesLoading} sx={{ textTransform: "none" }}>
              {mergeFilesLoading ? "Loading…" : "Refresh"}
            </Button>
            <Button
              size="small"
              variant={mergeFilesShowSub ? "contained" : "outlined"}
              onClick={() => setMergeFilesShowSub((v) => !v)}
              sx={{ textTransform: "none" }}
            >
              {mergeFilesShowSub ? "Hide Sub Files" : "Show Sub Files"}
            </Button>
            <Chip label={`${mergeFiles.length} file(s)`} />
          </Stack>
          <Stack spacing={0.75} sx={{ maxHeight: "60vh", overflowY: "auto" }}>
            {mergeFilesLoading ? (
              <Typography variant="body2">Loading…</Typography>
            ) : mergeFiles.length === 0 ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>No final merge PDF or SKU workbook found.</Typography>
            ) : mergeFiles.map((f) => (
              <Box key={f.rel} sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "34px 1fr auto" },
                gap: 1,
                alignItems: "center",
                border: "1px solid rgba(0,0,0,0.12)",
                borderRadius: 1,
                p: 1,
              }}>
                <Box sx={{ fontSize: 24, color: /\.xlsx?$/i.test(f.name) ? "success.main" : "error.main",
                           display: "flex", alignItems: "center" }}>
                  {/\.xlsx?$/i.test(f.name) ? <FaFileExcel /> : <FaFilePdf />}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: "break-all" }}>
                    {f.name}
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block", opacity: 0.7, wordBreak: "break-all" }}>
                    {f.date} · {f.folder} · {fmtSize(f.size)} · {fmtTime(f.modified)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="contained" onClick={() => openMynFile(f.rel, /\.xlsx?$/i.test(f.name))}
                    sx={{ textTransform: "none" }}>{/\.xlsx?$/i.test(f.name) ? "Open Excel" : "Open"}</Button>
                  <Button size="small" variant="outlined" onClick={() => openMynFile(f.rel, true)}
                    sx={{ textTransform: "none" }}>Download</Button>
                </Stack>
              </Box>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onClose={() => !resetBusy && setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: 16 }}>🔄 Reset Browser</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {accounts.map((a) => (
                <FormControlLabel key={a.profile} sx={{ m: 0, flex: "1 1 45%", minWidth: 140 }}
                  control={<Checkbox size="small" checked={resetProfiles.includes(a.profile)}
                    disabled={resetBusy}
                    onChange={() => toggle(resetProfiles, setResetProfiles, a.profile)} />}
                  label={<span style={{ fontSize: 12.5 }}>{a.name.replace(/^MYN /, "")}{!a.running ? " (off)" : ""}</span>} />
              ))}
            </Box>
            {resetResult ? (
              resetResult.ok ? (
                <Box sx={{ bgcolor: "#e6f4ea", color: "#1e4620", p: 1, borderRadius: 1, fontSize: 12.5 }}>
                  Reset done
                  {(resetResult.results || []).map((r) => (
                    <Box key={r.profile || r.account} sx={{ mt: 0.25 }}>
                      {r.account}: {r.ok ? "OK" : String(r.error || "failed")}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ bgcolor: "#fff3cd", color: "#664d03", p: 1, borderRadius: 1, fontSize: 12.5 }}>
                  {String(resetResult.error || "reset failed")}
                  {(resetResult.results || []).map((r) => (
                    <Box key={r.profile || r.account} sx={{ mt: 0.25 }}>
                      {r.account}: {r.ok ? "OK" : String(r.error || "failed")}
                    </Box>
                  ))}
                </Box>
              )
            ) : null}
          </Stack>
        </DialogContent>
        <Box sx={{ p: 1.5, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button size="small" onClick={() => setResetOpen(false)} disabled={resetBusy}
            sx={{ textTransform: "none" }}>Close</Button>
          <Button size="small" variant="contained" onClick={submitResetBrowsers}
            disabled={resetBusy || !resetProfiles.length} sx={{ textTransform: "none" }}>
            {resetBusy ? "Resetting…" : "Reset"}
          </Button>
        </Box>
      </Dialog>

      {/* Order Process — the desktop "Run Account" flow (multi-account) */}
      <Dialog open={procOpen} onClose={() => setProcOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: 16 }}>▶ Run Account</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Box sx={{ bgcolor: "#fff3cd", color: "#664d03", p: 1, borderRadius: 1, fontSize: 12, lineHeight: 1.35 }}>
              ⚠ Runs the <b>real packing flow</b> (autopicklist → order processor), assigns seals
              + prints labels. The local Mac DB is the source — only run for accounts the
              <b> desktop is not also processing</b>, or you’ll create duplicate/suspense packs.
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>Account(s)</Typography>
                <Box sx={{ flex: 1 }} />
                <Button size="small" variant="text" sx={{ textTransform: "none", minWidth: 0, fontSize: 11 }}
                  onClick={() => { const all = accounts.filter((x) => x.running).map((x) => x.profile); setProcAccounts(all); setProcParallel(all.length > 1); }}>All</Button>
                <Button size="small" variant="text" sx={{ textTransform: "none", minWidth: 0, fontSize: 11 }}
                  onClick={() => { setProcAccounts([]); setProcParallel(false); }}>None</Button>
              </Stack>
              <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", columnGap: 0.5,
                     border: "1px solid rgba(0,0,0,0.15)", borderRadius: 1, p: 0.5 }}>
                {accounts.map((a) => (
                  <FormControlLabel key={a.profile} sx={{ m: 0, flex: "1 1 30%", minWidth: 150 }}
                    control={<Checkbox size="small" checked={procAccounts.includes(a.profile)}
                      disabled={!a.running}
                      onChange={() => {
                        const next = procAccounts.includes(a.profile)
                          ? procAccounts.filter((p) => p !== a.profile)
                          : [...procAccounts, a.profile];
                        setProcAccounts(next);
                        setProcParallel(next.length > 1);   // auto-tick for 2+, off for ≤1
                      }} />}
                    label={<span style={{ fontSize: 12.5 }}>{a.name}{!a.running ? " (off)" : ""}</span>} />
                ))}
              </Box>
            </Box>

            <FormControlLabel sx={{ m: 0 }}
              control={<Checkbox size="small" checked={procParallel}
                disabled={procAccounts.length < 2}
                onChange={(e) => setProcParallel(e.target.checked)} />}
              label={<span style={{ fontSize: 12.5 }}>Run selected accounts in parallel</span>} />
            <Typography variant="caption" sx={{ opacity: 0.7, mt: -0.5 }}>
              Serial (one at a time) is safest for seals. Parallel runs each account on its own browser.
            </Typography>

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>Process orders up to this date &amp; time</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, alignItems: "center", flexWrap: "wrap" }}>
                <TextField size="small" label="Date (DD-MM-YYYY)" value={procDate}
                  onChange={(e) => setProcDate(e.target.value)} sx={{ maxWidth: 160 }} />
                <TextField size="small" label="Time (HH:MM)" value={procTime}
                  onChange={(e) => setProcTime(e.target.value)} sx={{ maxWidth: 110 }} />
                <Button size="small" variant="outlined" sx={{ textTransform: "none" }}
                  onClick={() => {
                    const now = new Date(); const pad = (n) => String(n).padStart(2, "0");
                    setProcDate(`${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}`);
                    setProcTime(`${pad(now.getHours())}:${pad(now.getMinutes())}`);
                  }}>Set to Now</Button>
              </Stack>
            </Box>

            <Box sx={{ bgcolor: "action.hover", p: 1, borderRadius: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                Extra processing profiles/workers are <b>not available in this local Mac console</b> (only the
                6 logged-in browsers). Each account runs on its own browser; parallel is across accounts.
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>Also run</Typography>
              <Stack sx={{ mt: 0.25 }}>
                <FormControlLabel sx={{ m: 0 }}
                  control={<Checkbox size="small" checked={procPreSku}
                    onChange={(e) => setProcPreSku(e.target.checked)} />}
                  label={<span style={{ fontSize: 12.5 }}>Pre SKU list (generate before processing)</span>} />
                <FormControlLabel sx={{ m: 0 }}
                  control={<Checkbox size="small" checked={procIntransit}
                    onChange={(e) => setProcIntransit(e.target.checked)} />}
                  label={<span style={{ fontSize: 12.5 }}>In-Transit download (after processing)</span>} />
                <FormControlLabel sx={{ m: 0 }}
                  control={<Checkbox size="small" checked={procOtc}
                    onChange={(e) => setProcOtc(e.target.checked)} />}
                  label={<span style={{ fontSize: 12.5 }}>OTC fetch + Scan update (after processing)</span>} />
                <FormControlLabel sx={{ m: 0 }}
                  control={<Checkbox size="small" checked={procMerge}
                    onChange={(e) => setProcMerge(e.target.checked)} />}
                  label={<span style={{ fontSize: 12.5 }}>Merge PDF after processing</span>} />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <Box sx={{ p: 1.5, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button size="small" onClick={() => setProcOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button size="small" variant="contained" onClick={submitProcess}
            disabled={!procAccounts.length || !procDate || !procTime} sx={{ textTransform: "none" }}>
            ▶ Run Now
          </Button>
        </Box>
      </Dialog>

      {/* Seal Add — add seals (range or paste) + auto barcode PDF */}
      <Dialog open={sealAddOpen} onClose={() => setSealAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: 16 }}>➕ Add Seals</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Typography variant="caption" sx={{ opacity: 0.75, lineHeight: 1.35 }}>
              Enter a <b>range</b> (e.g. <code>MP210589570-MP210589600</code>) or paste one
              seal per line. Ranges expand with zero-padding; duplicates are skipped. A Code128
              barcode PDF is generated for the new seals.
            </Typography>
            <TextField
              multiline minRows={4} maxRows={10} size="small"
              placeholder={"MP210589570-MP210589600\nor one seal per line"}
              value={sealAddText}
              onChange={(e) => setSealAddText(e.target.value)}
            />
            {sealAddResult ? (
              sealAddResult.ok ? (
                <Box sx={{ bgcolor: "#e6f4ea", color: "#1e4620", p: 1, borderRadius: 1, fontSize: 12.5 }}>
                  ✅ Added <b>{sealAddResult.added}</b> seal(s)
                  {sealAddResult.skipped ? ` · ${sealAddResult.skipped} duplicate(s) skipped (not re-added)` : " · no duplicates"}
                  {" · "}pool now <b>{sealAddResult.unused_now}</b> unused
                  {sealAddResult.dup_in_db_count ? (
                    <Box sx={{ mt: 0.5, color: "#7a4f01" }}>
                      Already in DB (skipped): {(sealAddResult.dup_in_db || []).slice(0, 20).join(", ")}
                      {sealAddResult.dup_in_db_count > 20 ? ` +${sealAddResult.dup_in_db_count - 20} more` : ""}
                    </Box>
                  ) : null}
                  {sealAddResult.dup_in_input ? (
                    <Box sx={{ mt: 0.25, color: "#7a4f01" }}>
                      {sealAddResult.dup_in_input} repeated in your input (counted once).
                    </Box>
                  ) : null}
                  {sealAddResult.pdf ? (
                    <Box sx={{ mt: 0.5 }}>
                      <Button size="small" variant="contained" onClick={() => openMynFile(sealAddResult.pdf, false)}
                        sx={{ textTransform: "none", mr: 1 }}>Open barcode PDF</Button>
                      <Button size="small" variant="outlined" onClick={() => openMynFile(sealAddResult.pdf, true)}
                        sx={{ textTransform: "none" }}>Download</Button>
                    </Box>
                  ) : null}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: "error.main" }}>
                  Error: {String(sealAddResult.error)}
                </Typography>
              )
            ) : null}
          </Stack>
        </DialogContent>
        <Box sx={{ p: 1.5, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button size="small" onClick={() => setSealAddOpen(false)} sx={{ textTransform: "none" }}>Close</Button>
          <Button size="small" variant="contained" onClick={submitSealAdd}
            disabled={sealAddBusy || !sealAddText.trim()} sx={{ textTransform: "none" }}>
            {sealAddBusy ? "Adding…" : "Add seals"}
          </Button>
        </Box>
      </Dialog>

      {/* Scheduler — grouped view (one time → many accounts) + editor */}
      <Dialog open={schedOpen} onClose={() => setSchedOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ py: 1, fontSize: 16, display: "flex", alignItems: "center", gap: 1 }}>
          <span>🗓️ Scheduler</span>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" onClick={loadSchedules} sx={{ textTransform: "none" }}>Refresh</Button>
          <Button size="small" variant="contained" onClick={startAddSched} sx={{ textTransform: "none" }}>+ Add</Button>
        </DialogTitle>
        <DialogContent dividers>
          {schedBusy ? (
            <Typography variant="body2">Loading…</Typography>
          ) : !schedData ? (
            <Typography variant="body2">Could not load schedules.</Typography>
          ) : (
            <>
              <Typography variant="caption" sx={{ opacity: 0.7, display: "block", mb: 0.5 }}>
                One schedule time can target multiple accounts. Edit changes time/days/accounts/steps.
              </Typography>
              <Box sx={{ overflow: "auto", maxHeight: "42vh",
                         border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1 }}>
                <Box component="table" sx={{ borderCollapse: "collapse", fontSize: 11.5, width: "100%" }}>
                  <Box component="thead">
                    <Box component="tr">
                      {["Time", "Days", "Accounts", "SKU", "Merge", "Print", "In Transit", "OTC", "On", ""].map((h) => (
                        <Box component="th" key={h}
                          sx={{ position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1,
                                borderBottom: "2px solid rgba(0,0,0,0.25)", px: 1, py: 0.5,
                                textAlign: "left", fontWeight: 700, whiteSpace: "nowrap" }}>
                          {h}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {schedData.groups.map((g, gi) => (
                      <Box component="tr" key={gi}
                        sx={{ "&:nth-of-type(even)": { bgcolor: "action.hover" }, opacity: g.enabled ? 1 : 0.5 }}>
                        <Box component="td" sx={{ px: 1, py: 0.4, fontWeight: 700 }}>{g.time}</Box>
                        <Box component="td" sx={{ px: 1, py: 0.4, whiteSpace: "nowrap" }}>
                          {(g.days || []).map((d) => d[0].toUpperCase()).join("")}
                        </Box>
                        <Box component="td" title={(g.account_names || []).join(", ")}
                          sx={{ px: 1, py: 0.4, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {(g.account_names || []).join(", ")}
                        </Box>
                        {["sku", "merge", "print", "transit_dl", "transit_otc"].map((k) => (
                          <Box component="td" key={k} sx={{ px: 1, py: 0.4, textAlign: "center" }}>
                            {g[k] ? "✓" : "–"}
                          </Box>
                        ))}
                        <Box component="td" sx={{ px: 1, py: 0.4, textAlign: "center" }}>{g.enabled ? "✓" : "✗"}</Box>
                        <Box component="td" sx={{ px: 1, py: 0.4, whiteSpace: "nowrap" }}>
                          <Button size="small" variant="text" onClick={() => startEditSched(g)}
                            sx={{ textTransform: "none", minWidth: 0, px: 0.75 }}>Edit</Button>
                          <Button size="small" variant="text" onClick={() => toggleSched(g)}
                            sx={{ textTransform: "none", minWidth: 0, px: 0.75 }}>{g.enabled ? "Off" : "On"}</Button>
                          <Button size="small" variant="text" color="error" onClick={() => deleteSched(g)}
                            sx={{ textTransform: "none", minWidth: 0, px: 0.75 }}>Del</Button>
                        </Box>
                      </Box>
                    ))}
                    {!schedData.groups.length ? (
                      <Box component="tr"><Box component="td" colSpan={10} sx={{ px: 1, py: 1, opacity: 0.6 }}>No schedules. Click “+ Add”.</Box></Box>
                    ) : null}
                  </Box>
                </Box>
              </Box>

              {schedEdit ? (
                <Box sx={{ mt: 1.5, p: 1.5, border: "1px solid rgba(0,0,0,0.2)", borderRadius: 1, bgcolor: "action.hover" }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {schedEdit.mode === "add" ? "New schedule" : `Edit ${schedEdit.time} schedule`}
                  </Typography>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <Stack spacing={1} sx={{ minWidth: 200 }}>
                      <TextField size="small" label="Time (HH:MM)" value={schedEdit.time}
                        onChange={(e) => setSchedEdit({ ...schedEdit, time: e.target.value })}
                        placeholder="09:00" sx={{ maxWidth: 130 }} />
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>Days</Typography>
                      <Stack direction="row" spacing={0.25} sx={{ flexWrap: "wrap" }}>
                        {DAYS7.map((d) => (
                          <Chip key={d} size="small" label={d[0].toUpperCase() + d[1]}
                            color={schedEdit.days.includes(d) ? "primary" : "default"}
                            onClick={() => setSchedEdit({
                              ...schedEdit,
                              days: schedEdit.days.includes(d)
                                ? schedEdit.days.filter((x) => x !== d)
                                : [...schedEdit.days, d],
                            })}
                            sx={{ cursor: "pointer", mb: 0.25 }} />
                        ))}
                      </Stack>
                    </Stack>
                    <Stack spacing={0.25} sx={{ minWidth: 180 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>Accounts</Typography>
                      {(schedData.accounts || []).map((a) => (
                        <FormControlLabel key={a.id} sx={{ m: 0 }}
                          control={<Checkbox size="small" checked={schedEdit.account_ids.includes(a.id)}
                            onChange={() => setSchedEdit({
                              ...schedEdit,
                              account_ids: schedEdit.account_ids.includes(a.id)
                                ? schedEdit.account_ids.filter((x) => x !== a.id)
                                : [...schedEdit.account_ids, a.id],
                            })} />}
                          label={<span style={{ fontSize: 12.5 }}>{a.name}</span>} />
                      ))}
                    </Stack>
                    <Stack spacing={0.25} sx={{ minWidth: 220 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>Steps after run</Typography>
                      {[
                        ["sku", "Generate Current SKU List"],
                        ["merge", "Merge All"],
                        ["print", "Open PDF + Print"],
                        ["transit_dl", "In Transit Download"],
                        ["transit_otc", "OTC Fetch + Scan Update"],
                        ["enabled", "Enabled"],
                      ].map(([k, lbl]) => (
                        <FormControlLabel key={k} sx={{ m: 0 }}
                          control={<Checkbox size="small" checked={!!schedEdit[k]}
                            onChange={() => setSchedEdit({ ...schedEdit, [k]: !schedEdit[k] })} />}
                          label={<span style={{ fontSize: 12.5 }}>{lbl}</span>} />
                      ))}
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: "flex-end" }}>
                    <Button size="small" onClick={() => setSchedEdit(null)} sx={{ textTransform: "none" }}>Cancel</Button>
                    <Button size="small" variant="contained" onClick={saveSched}
                      disabled={schedSaving || !schedEdit.account_ids.length || !schedEdit.days.length}
                      sx={{ textTransform: "none" }}>
                      {schedSaving ? "Saving…" : "Save"}
                    </Button>
                  </Stack>
                  {schedEdit.print && !schedEdit.merge ? (
                    <Typography variant="caption" sx={{ color: "warning.main", display: "block", mt: 0.5 }}>
                      Print implies Merge — Merge will be enabled automatically.
                    </Typography>
                  ) : null}
                </Box>
              ) : null}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Log — run history + LOGS files */}
      <Dialog
        open={logsOpen}
        onClose={() => setLogsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...(logsDialogPos
              ? { position: "fixed", top: logsDialogPos.y, left: logsDialogPos.x, m: 0 }
              : { position: "relative" }),
            ...(logsDialogSize
              ? {
                  width: logsDialogSize.width,
                  maxWidth: "calc(100vw - 16px)",
                  height: logsDialogSize.height,
                  maxHeight: "calc(100vh - 16px)",
                }
              : {}),
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          onPointerDown={startLogsDialogDrag}
          onDoubleClick={() => setLogsDialogPos(null)}
          title="Hold and drag to move. Double-click to center."
          sx={{ py: 1, fontSize: 16, cursor: "grab", userSelect: "none", touchAction: "none" }}
        >📜 Logs & run history</DialogTitle>
        <DialogContent dividers sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          {logsBusy ? (
            <Typography variant="body2">Loading…</Typography>
          ) : (
            <Stack spacing={1.5}>
              <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.8 }}>
                Recent runs (run_log)
              </Typography>
              {!runLogRows || !runLogRows.rows?.length ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>No run history.</Typography>
              ) : (
                <Box sx={{ overflow: "auto", maxHeight: "32vh",
                           border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1 }}>
                  <Box component="table" sx={{ borderCollapse: "collapse", fontSize: 11, width: "max-content", minWidth: "100%" }}>
                    <Box component="thead">
                      <Box component="tr">
                        {runLogRows.columns.map((c) => (
                          <Box component="th" key={c}
                            sx={{ position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1,
                                  borderBottom: "2px solid rgba(0,0,0,0.25)", px: 1, py: 0.5,
                                  textAlign: "left", fontWeight: 700, whiteSpace: "nowrap" }}>
                            {c}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {runLogRows.rows.map((r, ri) => (
                        <Box component="tr" key={ri}
                          sx={{ "&:nth-of-type(even)": { bgcolor: "action.hover" } }}>
                          {r.map((cell, ci) => (
                            <Box component="td" key={ci} title={String(cell)}
                              sx={{ px: 1, py: 0.3, borderBottom: "1px solid rgba(0,0,0,0.08)",
                                    whiteSpace: "nowrap", maxWidth: 280, overflow: "hidden",
                                    textOverflow: "ellipsis" }}>
                              {String(cell)}
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}
              <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.8, pt: 0.5 }}>
                Log files (newest first)
              </Typography>
              {!logsFiles || !logsFiles.length ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>No log files.</Typography>
              ) : (
                <Stack spacing={0.25} sx={{ maxHeight: "28vh", overflowY: "auto" }}>
                  {logsFiles.map((f) => (
                    <Stack key={f.name} direction="row" spacing={1} alignItems="center">
                      <Button size="small" variant="text" onClick={() => openMynFile(`LOGS/${f.name}`, false)}
                        sx={{ textTransform: "none", fontSize: 11, minWidth: 0, justifyContent: "flex-start", flex: 1 }}
                        title="Open">
                        {f.name}
                      </Button>
                      <Typography variant="caption" sx={{ opacity: 0.6, whiteSpace: "nowrap" }}>
                        {(f.size / 1024).toFixed(0)} KB
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
        <Box
          onPointerDown={startLogsDialogResize}
          title="Hold and drag corner to resize."
          sx={{
            position: "absolute",
            right: 0,
            bottom: 0,
            width: 24,
            height: 24,
            cursor: "nwse-resize",
            zIndex: 3,
            "&::after": {
              content: '""',
              position: "absolute",
              right: 6,
              bottom: 6,
              width: 10,
              height: 10,
              borderRight: "2px solid rgba(0,0,0,0.45)",
              borderBottom: "2px solid rgba(0,0,0,0.45)",
            },
          }}
        />
      </Dialog>

      <ConsoleDownloadsDialog
        open={dlOpen}
        onClose={() => setDlOpen(false)}
        source="myntra"
        title="📥 Myntra browser downloads"
      />
    </Box>
  );
}
